import { CalendarOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cGradeOf, levelRank } from '@shared/levels';
import { api } from '../api/client.js';
import { PersonCard, levelLabel } from '../pages/PersonCard.js';
import { SuccessionPlanView } from './SuccessionPlanView.js';
import type { RetirementBucket } from '@shared/types';

/**
 * การเกษียณและความต่อเนื่องของงาน — เปิดจากไอคอนในหัวแผงบัญชีอัตรา
 *
 * หน้านี้ทำตามแบบที่ออกแบบไว้ในแคนวาส "การเกษียณและความต่อเนื่องของงาน" (artboard Main)
 * โครงเดียวกันทุกส่วน: พาดหัวจำนวน → การ์ดกราฟซ้าย + การ์ดสรุปปีที่เลือกขวา →
 * ตารางอัตราที่จะว่างในปีนั้น → ที่มาของค่าที่ไม่ได้มาจากทะเบียนตรง ๆ
 *
 * ────────────────────────────────────────────────────────────────
 *  ส่วนที่แบบมี แต่หน้านี้ไม่มี — และทำไม
 * ────────────────────────────────────────────────────────────────
 *
 * แบบมีสามคอลัมน์ที่ระบบยังไม่มีข้อมูลเลยสักที่
 *
 *   ความสำคัญของงาน   ต้องมีการประเมินของ HR ต่ออัตรา
 *   ผู้ทดแทน          ต้องมีแนวคิด "ผู้ทดแทนที่ยืนยันแล้ว" ซึ่งยังไม่มีทั้งตาราง ฟิลด์ และปลายทาง
 *   แผนถ่ายทอดงาน     ต้องมีแผนต่ออัตราและรายการงานที่แก้ไขได้
 *
 * วันนี้ระบบอ่านอย่างเดียวจาก dump ของ DPIS · successionMatrix เป็นแค่ตารางนับ
 *
 * ⚠ ตัดทิ้งดีกว่าใส่คอลัมน์ว่างหรือใส่ค่าที่เดาเอา — ตารางที่มีคอลัมน์ "ผู้ทดแทน"
 *   แล้วทุกแถวขึ้นว่า "ยังไม่ยืนยัน" อ่านได้ว่า "ยังไม่มีใครทำ" ทั้งที่ความจริงคือ
 *   "ระบบยังไม่มีที่ให้กรอก" — สองอย่างนี้คนละเรื่องกันคนละขั้ว
 *
 * ⚠ วันที่หลังบ้านมีสามก้อนนั้นแล้ว ให้เติมคอลัมน์กลับตามลำดับในแบบ และย้ายสองแถว
 *   ในการ์ดขวากลับไปเป็น "งานสำคัญต่อภารกิจ" กับ "ยังไม่มีผู้ทดแทนที่ยืนยันแล้ว"
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ทำไมตารางไม่มีชื่อผู้ครอง
 * ────────────────────────────────────────────────────────────────
 *
 * แบบก็ไม่มี และไม่ได้เป็นเรื่องบังเอิญ
 *
 * "ชื่อคน + วันที่" เป็นรูปแบบที่แคปหน้าจอแล้วแนบเรื่องเสนอผู้บริหารได้ทันที หน้าจอที่
 * แสดงแบบนั้นต้องมีแถบเตือนเรื่องที่มาของวันเกิดกำกับเหนือรายชื่อเสมอ และแถบนั้นต้อง
 * ตัดสินจาก birthDateSource ที่เซิร์ฟเวอร์ส่งมา (ดูคำเตือนยาวใน RetirementRosterModal.tsx
 * ซึ่งเป็นเจ้าของเรื่องรายชื่อ) /api/retirement ไม่ได้ส่งค่านั้นมาด้วย
 *
 * หน้านี้จึงเป็นตารางของ "อัตรา" ไม่ใช่ของ "คน" — ตำแหน่ง หน่วยงาน วันที่ว่าง
 *
 * ────────────────────────────────────────────────────────────────
 *  ยอดรวมต้องตรงกับเลขบนการ์ด
 * ────────────────────────────────────────────────────────────────
 *
 * ผู้ใช้กด "เกษียณอีก 5 ปี 374 อัตรา" แล้วกดไอคอนนี้ต่อในหัวแผงเดียวกัน ผลรวมของทุกแท่ง
 * ต้องเป็น 374 เป๊ะ ๆ ถ้าไม่ตรง เขาจะไม่รู้ว่าอันไหนผิด แล้วเลิกเชื่อทั้งสองอัน
 *
 * ที่ทำให้ตรงได้คือทั้งสองฝั่งกรองด้วย underChartPath ตัวเดียวกัน และคัดคนด้วย
 * retiringWithin ตัวเดียวกัน ไม่ได้ตรงเพราะบังเอิญเขียนเงื่อนไขเหมือนกัน
 * (server/scripts/check-retire-chart.ts เดินผังทั้งต้นแล้วเทียบยอดทุกโหนด)
 */

/** เท่ากับ RETIRE_HORIZON_YEARS ของเซิร์ฟเวอร์ — ระยะเดียวกับที่การ์ดในผังใช้ */
const HORIZON = 5;

/** ระดับที่ถือว่า "ระดับสูง" — ตรงกับ isSeniorLevel ของเซิร์ฟเวอร์ที่ใช้นับ seniorCount */
const SENIOR_WORDS = 'บริหาร · อำนวยการ · ทรงคุณวุฒิ · เชี่ยวชาญ';

export function RetirementWaveModal({
  unitPath,
  unitName,
  onClose,
  asPanel = false,
  panelWidth = null,
  panelTools,
  panelGrip,
}: {
  /** รหัสโหนดจากผัง (OrgNode.id) — ไม่ใช่ชื่อหน่วยที่พิมพ์เอง */
  unitPath: string;
  unitName: string;
  onClose: () => void;
  /** วาดเป็นแผงเปิดขวา (.plp) แทนหน้าต่างกลางจอ — ไม่ล็อกการเลื่อนของหน้า เพราะผังข้าง ๆ ยังใช้งานได้ */
  asPanel?: boolean;
  /** ความกว้างแผง (px) — null = แบ่งครึ่งตาม CSS */
  panelWidth?: number | null;
  /** ปุ่มเพิ่มในหัวแผง (ย้ายแผงซ้าย/ขวา) — ใช้เฉพาะ asPanel */
  panelTools?: ReactNode;
  /** เส้นจับลากปรับความกว้าง — ใช้เฉพาะ asPanel */
  panelGrip?: ReactNode;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  /* สามชั้นในหน้าต่างเดียว: รายการเกษียณ → แผนของอัตราหนึ่ง → ประวัติของคนหนึ่ง
     ⚠ สลับเนื้อหาในหน้าต่างเดิม ไม่ได้เปิดหน้าต่างซ้อนเพิ่ม — หน้าต่างซ้อนสามชั้น
       ทำให้กด Esc แล้วเดาไม่ถูกว่าปิดชั้นไหน และปุ่มปิดจะซ้อนกันสามใบที่มุมเดียวกัน
     ⚠ เก็บ posId ไม่ใช่ทั้งก้อน Position เพราะตารางมีแค่ posId (RetirementBucket.people)
       ตัวหน้าแผนไปโหลดอัตราเต็มเอง แล้วจะได้ข้อมูลชุดเดียวกับหน้าอื่นเสมอ */
  const [planId, setPlanId] = useState<string | null>(null);
  const [who, setWho] = useState<{ personaId: string; name: string } | null>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);

  /* Esc ปิด · ล็อกการเลื่อนของหน้าหลัง · คืนโฟกัสให้ปุ่มที่เปิดหน้าต่างนี้ตอนปิด
     ⚠ แผงที่อยู่ข้างหลังก็ดัก Esc ที่ document เหมือนกัน การกันไม่ให้ปิดพร้อมกันอยู่ที่
       ฝั่งแผง (ตัวแปร waveOpen ใน PositionListPanel) ไม่ใช่ที่ stopPropagation ตรงนี้
       — stopPropagation หยุด listener ตัวอื่นที่ element เดียวกันไม่ได้ */
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    /* ⚠ Esc ถอยทีละชั้น ไม่ใช่ปิดรวดเดียว
       ปิดรวดเดียวจากหน้าประวัติจะพาผู้ใช้กลับไปถึงผังองค์กร ทั้งที่เขาแค่ดูประวัติ
       คนหนึ่งเสร็จแล้วอยากดูคนถัดไปในแผนเดิม (กฎเดียวกับที่ PositionListPanel ใช้)
       ⚠ ใช้ตัวอัปเดตแบบฟังก์ชันทุกชั้น เพราะ effect นี้ผูกกับ [onClose] เท่านั้น
         ถ้าอ่าน who/planId ตรง ๆ จะได้ค่าของตอนติดตั้ง listener ซึ่งเป็น null เสมอ */
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setWho((curWho) => {
        if (curWho) return null;
        setPlanId((curPlan) => {
          if (curPlan) return null;
          onClose();
          return null;
        });
        return null;
      });
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    if (!asPanel) document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      returnTo?.focus?.();
    };
  }, [onClose, asPanel]);

  const wave = useQuery({
    queryKey: ['retirement-wave', unitPath, HORIZON],
    queryFn: () => api.retirement(HORIZON, unitPath),
  });

  /* ตัวหารของพาดหัว — "374 คนจากผู้ครองอัตรา 4,608 คน"
     ⚠ ต้องเป็นยอด occupied ไม่ใช่ยอดกรอบทั้งหมด คนที่จะเกษียณต้องเป็นคนที่ครองอยู่
       ถ้าหารด้วยกรอบ (ซึ่งรวมอัตราว่าง) สัดส่วนจะต่ำกว่าความจริงเสมอ */
  const staff = useQuery({
    queryKey: ['unit-positions-total', unitPath, 'occupied'],
    queryFn: () => api.positions({ unitPath, status: 'occupied', limit: 1 }),
    select: (d) => d.total,
  });

  /* อัตราของหน้าแผน — โหลดเมื่อเปิดเท่านั้น (enabled)
     ⚠ อย่ารวมเข้ากับ queryKey ของกราฟ สองอันนี้อายุไม่เท่ากันและคนละขนาด */
  const plan = useQuery({
    queryKey: ['position', planId],
    queryFn: () => api.position(planId as string),
    enabled: planId !== null,
  });

  const buckets = useMemo(() => fillGaps(wave.data ?? []), [wave.data]);
  const total = buckets.reduce((n, b) => n + b.count, 0);
  const peak = buckets.reduce((m, b) => Math.max(m, b.count), 0);
  const axisMax = niceMax(peak);

  /* ปีที่เลือกตั้งต้น = ปีแรกที่มีคน ไม่ใช่ปีแรกของแกน
     ⚠ ห้ามตั้งต้นเป็น null แล้วให้ตารางข้างล่างว่าง — ตารางว่างตั้งแต่เปิดหน้ามา
       อ่านได้ว่า "ไม่มีอัตราจะว่าง" ทั้งที่กราฟข้างบนขึ้นเลขอยู่ */
  const firstWithPeople = buckets.find((b) => b.count > 0)?.fiscalYear ?? null;
  const year = picked ?? firstWithPeople;
  const shown = buckets.find((b) => b.fiscalYear === year);

  /* อัตราของปีที่เลือก เรียงระดับสูงขึ้นก่อน แล้วค่อยตามวันที่
     ⚠ เรียงด้วย levelRank ตัวเดียวกับที่บัญชีอัตราข้างหลังใช้ ไม่งั้นผู้ใช้ที่สลับไปมา
       ระหว่างสองหน้าจอจะเจอลำดับไม่เหมือนกันทั้งที่เป็นอัตราชุดเดียวกัน */
  const rows = useMemo(() => {
    const list = [...(shown?.people ?? [])];
    list.sort(
      (a, b) =>
        levelRank(a.level) - levelRank(b.level) ||
        a.retireDate.localeCompare(b.retireDate) ||
        a.title.localeCompare(b.title, 'th'),
    );
    return list;
  }, [shown]);

  const seniorRows = rows.filter((p) => isSenior(p.level)).length;
  const unitsHit = new Set(rows.map((p) => p.unit || 'ไม่ระบุหน่วยงาน')).size;

  /* แจกแจงระดับของปีที่เลือก — เรียงตามความอาวุโส ไม่ใช่ตามจำนวน
     แถวบนสุดต้องเป็นระดับสูงสุดเสมอ ไม่ใช่สลับเองเมื่อข้อมูลเปลี่ยน */
  const levels = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of rows) {
      const k = p.level || 'ไม่ระบุระดับ';
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return [...m.entries()].sort(
      ([a], [b]) => levelRank(a) - levelRank(b) || a.localeCompare(b, 'th'),
    );
  }, [rows]);

  /* เนื้อหาชุดเดียว ใช้ได้ทั้งหน้าต่างกลางจอ และแผงเปิดขวา (asPanel) */
  const content = (
    <>
        <header className="pmodal-hd">
          {/* ปุ่มถอยอยู่ในหัวหน้าต่าง ไม่ใช่ท้ายเนื้อหา — หน้าแผนยาวเกินหนึ่งจอ
              ปุ่มที่ต้องเลื่อนลงไปหาถึงจะเจอ เท่ากับไม่มีปุ่ม */}
          {who && (
            <button type="button" className="pmodal-back-btn" onClick={() => setWho(null)}>
              <i aria-hidden="true" /> กลับไปที่แผน
            </button>
          )}
          {!who && planId && (
            <button type="button" className="pmodal-back-btn" onClick={() => setPlanId(null)}>
              <i aria-hidden="true" /> กลับไปที่รายการเกษียณ
            </button>
          )}
          {!who && !planId && (
            <span className="simicon" aria-hidden="true"><CalendarOutlined /></span>
          )}

          <div>
            {who ? (
              <>
                <span className="kind">ประวัติรายบุคคล</span>
                <b>{who.name}</b>
                <span className="sub">ประกอบการพิจารณาผู้ทดแทนของอัตราที่กำลังดูแผนอยู่</span>
              </>
            ) : planId ? (
              <>
                <span className="kind">แผนผู้ทดแทนและการถ่ายทอดงาน</span>
                <b>{plan.data?.title ?? 'กำลังโหลด…'}</b>
                <span className="sub">
                  {plan.data
                    ? `${plan.data.level || 'ไม่ระบุระดับ'}${plan.data.posNo ? ` · เลขที่ ${plan.data.posNo}` : ''} — ${plan.data.orgPath[plan.data.orgPath.length - 1] ?? plan.data.unit}`
                    : ''}
                </span>
              </>
            ) : (
              <>
                <span className="kind">การเกษียณและความต่อเนื่องของงาน</span>
                <b>{unitName}</b>
                <span className="sub">
                  ดูว่าอัตราไหนจะว่างลงในแต่ละรอบ 1 ต.ค. จากวันเกษียณของผู้ครองปัจจุบัน
                </span>
              </>
            )}
          </div>

          {/* ⚠ ป้ายสถานะเป็นทอง ไม่ใช่เขียว และเขียนว่า "ยังไม่มีแผนที่บันทึกไว้"
              ไม่ใช่ "แผนยังไม่ยืนยัน" ตามแบบ — คำหลังบอกเป็นนัยว่ามีแผนอยู่แล้วรอยืนยัน
              ทั้งที่ระบบยังไม่มีที่เก็บแผนเลยสักที่ */}
          {!who && planId && (
            <span className="pmodal-pill">ยังไม่มีแผนที่บันทึกไว้ในระบบ</span>
          )}

          {asPanel && panelTools && <div className="plp-tools">{panelTools}</div>}
          <button type="button" onClick={onClose} aria-label="ปิดหน้าต่าง" ref={closeBtn}>✕</button>
        </header>

        {who ? (
          <div className="pmodal-body">
            <PersonCard personaId={who.personaId} embedded />
          </div>
        ) : planId ? (
          <div className="pmodal-body">
            {plan.isPending && <p className="state">กำลังโหลดข้อมูลอัตรา…</p>}
            {plan.isError && <p className="state error">{String(plan.error)}</p>}
            {plan.data && (
              <SuccessionPlanView
                pos={plan.data}
                onOpenProfile={(personaId, name) => setWho({ personaId, name })}
              />
            )}
          </div>
        ) : (
        <div className="pmodal-body rwv">
          {wave.isPending && <p className="state">กำลังโหลดข้อมูลการเกษียณ…</p>}
          {wave.isError && <p className="state error">{String(wave.error)}</p>}

          {wave.data !== undefined && total === 0 && (
            <p className="state">หน่วยงานนี้ไม่มีผู้ครองที่จะเกษียณภายใน {HORIZON} ปี</p>
          )}

          {wave.data !== undefined && total > 0 && (
            <>
              {/* ────────── พาดหัวจำนวน ──────────
                  เลขเด่นใช้ทอง เพราะทั้งหน้านี้พูดเรื่องเกษียณ ซึ่งระบบใช้ทองมาตลอด
                  ตัวหารต่อท้ายเป็นสีจาง — มันคือฐาน ไม่ใช่คำตอบ */}
              <div className="rwv-lede">
                <span className="n tnum">{total.toLocaleString('th-TH')}</span>
                <span className="k">คนที่จะเกษียณใน {HORIZON} ปีข้างหน้า</span>
                {staff.data !== undefined && (
                  <span className="d tnum">
                    จากผู้ครองอัตราในหน่วยงานนี้ {staff.data.toLocaleString('th-TH')} คน
                  </span>
                )}
              </div>

              {/* ────────── กราฟ + การ์ดสรุปปีที่เลือก ────────── */}
              <div className="rwv-top">
                <section className="rwv-card">
                  <div className="rwv-card-hd">
                    <h3>เกษียณแต่ละรอบ (อัตราว่าง 1 ต.ค.)</h3>
                    <span className="tnum">รวม {total.toLocaleString('th-TH')} คน</span>
                  </div>

                  {/* คอลัมน์ป้ายแกน + แผ่นพล็อต — ป้ายปีอยู่แถวที่สองของกริดเดียวกัน
                      จึงตรงกับแท่งเสมอ ไม่ต้องคำนวณตำแหน่งเอง */}
                  <div className="rwv-plot">
                    <div className="rwv-axis">
                      {ticksOf(axisMax).map((t, i, all) => (
                        <span
                          key={t}
                          className="tnum"
                          style={{ top: `${(i / (all.length - 1)) * 100}%` }}
                        >
                          {t.toLocaleString('th-TH')}
                        </span>
                      ))}
                    </div>

                    <div className="rwv-pane">
                      {/* เส้นระดับอยู่หลังแท่ง เส้นล่างสุดเข้มกว่าเพราะเป็นเส้นฐาน ไม่ใช่เส้นระดับ */}
                      {ticksOf(axisMax).map((t, i, all) => (
                        <i
                          key={t}
                          className={i === all.length - 1 ? 'rwv-grid is-base' : 'rwv-grid'}
                          style={{ top: `${(i / (all.length - 1)) * 100}%` }}
                        />
                      ))}

                      <ul className="rwv-bars">
                        {buckets.map((b) => {
                          const on = b.fiscalYear === year;
                          const h = axisMax > 0 ? (b.count / axisMax) * 100 : 0;
                          const sh = b.count > 0 ? (b.seniorCount / b.count) * 100 : 0;
                          return (
                            <li key={b.fiscalYear}>
                              <button
                                type="button"
                                className={`rwv-bar${on ? ' is-on' : ''}`}
                                aria-pressed={on}
                                onClick={() => setPicked(b.fiscalYear)}
                                aria-label={`เกษียณ 1 ต.ค. ${b.fiscalYear} · ${b.count} คน`}
                              >
                                <span className="n tnum">{b.count.toLocaleString('th-TH')}</span>
                                <span className="col" style={{ height: `${h}%` }}>
                                  {/* ส่วนล่างสีเข้มคือระดับสูง — สองเฉดของสีเดียวกัน
                                      อ่านว่า "ส่วนหนึ่งของแท่งนี้" ไม่ใช่ของอีกก้อน */}
                                  {b.seniorCount > 0 && <i style={{ height: `${sh}%` }} />}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    {/* ช่องเปล่าใต้คอลัมน์ป้ายแกน เพื่อให้แถวป้ายปีเริ่มตรงกับแผ่นพล็อต */}
                    <div />
                    <ul className="rwv-xlabels">
                      {buckets.map((b) => (
                        <li
                          key={b.fiscalYear}
                          className={`tnum${b.fiscalYear === year ? ' is-on' : ''}`}
                        >
                          {b.fiscalYear}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <p className="rwv-cap">
                    กดแท่งเพื่อเลือกปี — ตารางด้านล่างจะแสดงเฉพาะอัตราที่ว่างลงในปีนั้น ·
                    ส่วนสีเข้มที่ฐานแท่งคือระดับสูง ({SENIOR_WORDS})
                  </p>
                </section>

                {/* ────────── การ์ดสรุปปีที่เลือก ────────── */}
                <section className="rwv-card is-side">
                  <div className="rwv-card-hd">
                    <h3>
                      เกษียณ 1 ต.ค. <span className="tnum">{year}</span> ·{' '}
                      <span className="tnum">{rows.length.toLocaleString('th-TH')}</span> อัตรา
                    </h3>
                  </div>

                  {/* สองแถวที่บอกว่าปีนี้หนักแค่ไหน
                      ⚠ ใช้ทอง ไม่ใช่แดงอิฐ — แดงในระบบนี้แปลว่า "ต้องลงมือวันนี้"
                        ส่วนอัตราที่จะว่างในอนาคตแปลว่า "ต้องเตรียม" คนละน้ำหนักกัน */}
                  <div className="rwv-facts">
                    <div>
                      <span className="lb">ระดับสูงที่จะว่าง</span>
                      <span className="v tnum">{seniorRows.toLocaleString('th-TH')} อัตรา</span>
                    </div>
                    <div>
                      <span className="lb">กระจายอยู่ใน</span>
                      <span className="v tnum">{unitsHit.toLocaleString('th-TH')} หน่วยงาน</span>
                    </div>
                  </div>

                  <div className="rwv-lv">
                    <h4>แยกตามระดับ</h4>
                    <ul>
                      {levels.map(([level, n]) => {
                        const c = cGradeOf(level);
                        return (
                          <li key={level}>
                            <span className="lb">
                              {levelLabel(level)}
                              {c && <b className="cg">ซี {c}</b>}
                            </span>
                            <span className="bar">
                              <i style={{ width: `${(n / Math.max(rows.length, 1)) * 100}%` }} />
                            </span>
                            <span className="n tnum">{n.toLocaleString('th-TH')}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </section>
              </div>

              {/* ────────── ตารางอัตราที่จะว่าง ────────── */}
              <div className="rwv-tbl-hd">
                <h3>
                  อัตราที่จะว่าง 1 ต.ค. <span className="tnum">{year}</span>
                </h3>
                <span className="tnum">{rows.length.toLocaleString('th-TH')} อัตรา</span>
              </div>

              <div className="rwv-tbl">
                <table>
                  <thead>
                    <tr>
                      <th>ตำแหน่ง</th>
                      <th>หน่วยงาน</th>
                      <th>วันที่เกษียณ</th>
                      <th className="right">แผนถ่ายทอดงาน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => (
                      <tr key={p.posId} className={isSenior(p.level) ? 'is-senior' : undefined}>
                        <th scope="row">
                          <span className="t">{p.title}</span>
                          <span className="s tnum">
                            {p.level || 'ไม่ระบุระดับ'}
                            {p.posNo ? ` · เลขที่ ${p.posNo}` : ''}
                          </span>
                        </th>
                        <td>{p.unit || 'ไม่ระบุหน่วยงาน'}</td>
                        <td className="tnum nowrap">{thaiFull(p.retireDate)}</td>
                        <td className="right nowrap">
                          <button type="button" className="rwv-go" onClick={() => setPlanId(p.posId)}>
                            ดูแผน <span aria-hidden="true">→</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ที่มาของค่าที่ไม่ได้อ่านจากทะเบียนตรง ๆ — ระบบนี้บอกที่มาทุกค่า */}
              <p className="rwv-src">
                วันที่เกษียณคำนวณจากวันเกิดของผู้ครองตามกฎ ก.พ. ไม่ใช่การพยากรณ์ ·
                ยังไม่ได้หักผู้ที่อาจลาออกก่อนกำหนด · แถวที่มีเส้นทองด้านหน้าคือระดับสูง
              </p>
            </>
          )}
        </div>
        )}
    </>
  );

  /* แผงเปิดขวา — ช่องเดียวกับแผงบัญชีอัตรา/รายละเอียดอัตรา (ผู้ใช้สั่ง 19 ก.ย. 2569 สำหรับผังผู้บริหาร) */
  if (asPanel) {
    return (
      <aside
        className="plp"
        style={panelWidth == null ? undefined : { width: `${panelWidth}px` }}
        aria-label={`การเกษียณและความต่อเนื่องของงาน — ${unitName}`}
      >
        {panelGrip}
        <div className="plp-inner">{content}</div>
      </aside>
    );
  }

  return (
    <div
      className="pmodal-back is-flush"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="pmodal is-flush"
        role="dialog"
        aria-modal="true"
        aria-label={`การเกษียณและความต่อเนื่องของงาน — ${unitName}`}
      >
        {content}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function isSenior(level: string): boolean {
  return ['บริหาร', 'อำนวยการ', 'ทรงคุณวุฒิ', 'เชี่ยวชาญ'].some((w) => level.includes(w));
}

/** 30 ก.ย. 2571 */
const TH_MONTH = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];

function thaiFull(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '—';
  return `${Number(d)} ${TH_MONTH[Number(m) - 1] ?? ''} ${Number(y) + 543}`;
}

/**
 * เพดานแกนตั้งที่เป็นเลขกลม และหารด้วย 3 ลงตัว
 *
 * ⚠ ต้องเป็นเลขกลม ไม่ใช่ค่าสูงสุดจริง ถ้าใช้ค่าสูงสุดจริงเป็นเพดาน แท่งที่สูงสุด
 *   จะชนขอบบนเสมอทุกชุดข้อมูล แล้วผู้ใช้อ่านไม่ออกว่านั่นคือเพดานของแกน
 *   หรือกราฟถูกตัด และป้ายแกนจะกลายเป็นเลขอย่าง 112 / 74.7 / 37.3 / 0
 *
 * 3 ช่วง = ป้ายสี่ตัว (0 · 1/3 · 2/3 · เพดาน) ซึ่งเป็นจำนวนที่แบบใช้
 */
function niceMax(peak: number): number {
  if (peak <= 0) return 3;
  /* ⚠ ขั้นต่ำสุดของช่วงคือ 1 คน — ปีที่มีคนเดียวถ้าปล่อยให้คำนวณตามสูตร
     จะได้ช่วงเป็น 0.4 แล้วป้ายแกนกลายเป็น 1.2 / 0.8 / 0.4 / 0 ซึ่งอ่านไม่ได้
     เพราะหน่วยของแกนนี้คือ "คน" ที่เป็นจำนวนเต็มเสมอ */
  const need = Math.max(peak / 3, 1);
  const mag = Math.max(10 ** Math.floor(Math.log10(need)), 1);
  for (const f of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    if (f * mag >= need) return Math.max(1, Math.ceil(f * mag)) * 3;
  }
  return Math.ceil(10 * mag) * 3;
}

/** ป้ายแกนจากบนลงล่าง — เพดานอยู่บนสุด ศูนย์อยู่ล่างสุด */
function ticksOf(max: number): number[] {
  const step = max / 3;
  return [max, step * 2, step, 0];
}

/**
 * เติมปีที่ไม่มีใครเกษียณให้เป็นแท่งศูนย์
 *
 * ⚠ เซิร์ฟเวอร์ส่งมาเฉพาะปีที่มีคน ถ้าวาดตามนั้นตรง ๆ ปีที่ว่างจะหายไปจากแกน
 *   แล้ว 2569 กับ 2572 จะมาอยู่ติดกันเหมือนเป็นปีถัดกัน ซึ่งอ่านผิดทั้งกราฟ
 *
 * ⚠ เติมเฉพาะช่องว่างตรงกลางระหว่างปีแรกกับปีสุดท้ายที่มีข้อมูลจริง ไม่ได้คำนวณ
 *   ปีงบปัจจุบันขึ้นมาเองแล้วเติมหัวท้าย เพราะนั่นเท่ากับหน้าจอตั้งขอบเขตของกราฟเอง
 *   ซึ่งจะเริ่มไม่ตรงกับช่วงที่เซิร์ฟเวอร์คัดมาให้
 */
function fillGaps(raw: readonly RetirementBucket[]): RetirementBucket[] {
  if (raw.length === 0) return [];
  const sorted = [...raw].sort((a, b) => a.fiscalYear - b.fiscalYear);
  const first = sorted[0]!.fiscalYear;
  const last = sorted[sorted.length - 1]!.fiscalYear;

  const have = new Map(sorted.map((b) => [b.fiscalYear, b]));
  const out: RetirementBucket[] = [];
  for (let fy = first; fy <= last; fy += 1) {
    out.push(have.get(fy) ?? { fiscalYear: fy, count: 0, seniorCount: 0, people: [] });
  }
  return out;
}
